/**
 * 本文から `#tag` を見つける。
 *
 * Swift 版（`Packages/BisideCore/Sources/BisideCore/Tags/TagParser.swift`）の対応実装で、
 * **`cloud/fixtures/tag-parser.json` で照合される**。文法を変えるときは両方とフィクスチャを同時に直す。
 *
 * ## 文法（Swift 版の規則をそのまま）
 * - 本体に使えるのは Unicode 文字・ASCII 数字・`_`・`-`・`/`（階層）と絵文字。
 * - **文字・`_`・絵文字を最低 1 つ含む**こと。`#1984` は不可、`#v1984` と `#🧠` は可。
 * - `#` の直前が識別子文字（文字・数字・`_`）なら語中なのでタグにしない。
 * - 末尾の `/` は範囲から外す。`#` の直後が `/` のものはタグにしない。
 * - 突き合わせは小文字（`key`）、表示は元の大小（`display`）。
 *
 * ## 走査単位
 * 書記素クラスタで走る。国旗・肌色・ZWJ 連結・キーキャップ・異体字セレクタ付きを 1 文字として
 * 扱うため。位置は **UTF-16 単位**で返す（Swift の `NSRange` と JS の文字列添字が一致する単位）。
 */

export interface TagMatch {
  /** UTF-16 の開始位置。`#` を含む。 */
  location: number;
  /** UTF-16 の長さ。末尾の `/` を除いた範囲。 */
  length: number;
  /** 突き合わせ用に小文字化し、区切りを `/` で繋いだもの。 */
  key: string;
  /** 元の大小を保った表示用。 */
  display: string;
}

export interface Range16 {
  location: number;
  length: number;
}

const RE_ALPHABETIC = /^\p{Alphabetic}$/u;
const RE_EMOJI_PRESENTATION = /^\p{Emoji_Presentation}$/u;
const RE_EMOJI = /^\p{Emoji}$/u;

/**
 * 書記素クラスタへ分割する。`Intl.Segmenter` は Node と workerd の双方で使える。
 * 自前の実装を書かないのは、ZWJ 連結や異体字セレクタの扱いを取りこぼさないため。
 */
const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
function graphemes(text: string): string[] {
  const out: string[] = [];
  for (const s of segmenter.segment(text)) out.push(s.segment);
  return out;
}

function firstScalar(cluster: string): string {
  const cp = cluster.codePointAt(0);
  return cp === undefined ? "" : String.fromCodePoint(cp);
}

function isASCIIDigit(cluster: string): boolean {
  const cp = cluster.codePointAt(0);
  return cp !== undefined && cp >= 0x30 && cp <= 0x39;
}

/**
 * 絵文字クラスタか。先頭スカラーで判定するので、肌色や ZWJ で伸びた列も丸ごと 1 つの絵文字になる。
 * ASCII の `#` `*` や素の数字は単独スカラーの Emoji だが、絵文字表示ではないのでここには落ちない。
 */
function isEmojiCluster(cluster: string): boolean {
  const cp = cluster.codePointAt(0);
  if (cp === undefined) return false;
  if (cp >= 0x1f1e6 && cp <= 0x1f1ff) return true; // 地域表示記号（国旗）
  const first = firstScalar(cluster);
  if (RE_EMOJI_PRESENTATION.test(first)) return true;
  if ([...cluster].length > 1 && RE_EMOJI.test(first)) return true;
  return false;
}

/** 語の区切りを塞ぐ文字。絵文字は識別子ではないので、絵文字の直後の `#` はタグになる。 */
function isIdentifier(cluster: string): boolean {
  const first = firstScalar(cluster);
  return RE_ALPHABETIC.test(first) || isASCIIDigit(cluster) || first === "_";
}

/** タグ本体に使える文字。 */
function isBody(cluster: string): boolean {
  if (isEmojiCluster(cluster)) return true;
  const first = firstScalar(cluster);
  return (
    RE_ALPHABETIC.test(first) ||
    isASCIIDigit(cluster) ||
    first === "_" ||
    first === "-" ||
    first === "/"
  );
}

/** 「数字だけではない」を満たす文字。数字・`-`・`/` は数えない。 */
function isQualifying(cluster: string): boolean {
  if (isEmojiCluster(cluster)) return true;
  const first = firstScalar(cluster);
  return RE_ALPHABETIC.test(first) || first === "_";
}

function isExcluded(location: number, ranges: Range16[]): boolean {
  for (const r of ranges) {
    if (location >= r.location && location < r.location + r.length) return true;
  }
  return false;
}

export interface TagScanOptions {
  /** コードノードは丸ごと対象外。 */
  isCode?: boolean;
  /** この範囲の内側にある `#` は見ない（リンクなど）。 */
  excluding?: Range16[];
}

/** 本文中のタグをすべて返す。 */
export function tags(text: string, options: TagScanOptions = {}): TagMatch[] {
  const { isCode = false, excluding = [] } = options;
  if (isCode || text.length === 0 || !text.includes("#")) return [];

  const chars = graphemes(text);
  // UTF-16 長さの累積和。範囲を出すたびに文字列を歩き直さないため。
  const off: number[] = new Array(chars.length + 1);
  off[0] = 0;
  for (let k = 0; k < chars.length; k++) off[k + 1] = off[k] + chars[k].length;

  const matches: TagMatch[] = [];
  let i = 0;
  while (i < chars.length) {
    if (chars[i] !== "#") {
      i++;
      continue;
    }
    // 語の途中の `#` は無視する。
    const prev = i > 0 ? chars[i - 1] : null;
    if (prev !== null && isIdentifier(prev)) {
      i++;
      continue;
    }
    if (isExcluded(off[i], excluding)) {
      i++;
      continue;
    }

    let j = i + 1;
    let hasQualifying = false;
    while (j < chars.length && isBody(chars[j])) {
      if (isQualifying(chars[j])) hasQualifying = true;
      j++;
    }
    if (!(j > i + 1 && chars[i + 1] !== "/" && hasQualifying)) {
      i++;
      continue;
    }

    let end = j;
    while (end > i + 1 && chars[end - 1] === "/") end--;

    const body = chars.slice(i + 1, end).join("");
    const segments = body.split("/").filter((s) => s.length > 0);
    if (segments.length === 0) {
      i = j;
      continue;
    }
    const display = segments.join("/");
    matches.push({
      location: off[i],
      length: off[end] - off[i],
      key: display.toLowerCase(),
      display,
    });
    i = j; // 本体を丸ごと飛ばす
  }
  return matches;
}

/** 出現順で重複を畳んだ key の一覧。 */
export function tagKeys(text: string, options: TagScanOptions = {}): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of tags(text, options)) {
    if (!seen.has(m.key)) {
      seen.add(m.key);
      out.push(m.key);
    }
  }
  return out;
}

/**
 * タグを取り除いた表示用の文字列。タブ名やパンくずのような**短いラベル専用**で、
 * タグを見せたい面では使わない。
 */
export function strippedOfTags(text: string, options: TagScanOptions = {}): string {
  const matches = tags(text, options);
  if (matches.length === 0) return text;

  // 後ろから削ると、前方に残る範囲がずれない。
  let out = text;
  for (const m of [...matches].sort((a, b) => b.location - a.location)) {
    out = out.slice(0, m.location) + out.slice(m.location + m.length);
  }
  return out.replace(/[ \t]+/g, " ").trim();
}

/**
 * 手で打たれたタグ名を正規化する。`#` と前後の空白、末尾の `/` は許す。
 * 本体の文法に合わないもの（空白を含む、`/` 始まり、数字だけ）は null。
 */
export function normalize(input: string): { key: string; display: string } | null {
  let s = input.trim();
  if (s.startsWith("#")) s = s.slice(1);
  while (s.endsWith("/")) s = s.slice(0, -1);
  if (s.length === 0) return null;

  // 正規のパーサに `#<s>` を通し、本体を丸ごと覆えたときだけ受け入れる。
  const probe = "#" + s;
  const m = tags(probe)[0];
  if (!m || m.location !== 0 || m.length !== probe.length) return null;
  return { key: m.key, display: m.display };
}
